/**
 * SA-460 (workflow UX sweep spec §7.3): the record's process as a list —
 * one entry per ledger visit in order (a second visit to a step is a new
 * entry; a `process_step_reopened` event is a visit of its own), then the
 * current step with its open task, the next two steps in reading order and
 * the count of the rest; a closed record ends with its outcome. Pure. The
 * produced sentence reads the same source as the drawer's Recorded block
 * (`recordedFields`), so the two never disagree, and it respects redaction.
 */
import type { OrderEventItem, RecordSource } from '@/types/commerce'
import type { RecordProcessRead, TaskRead } from '@/types/process'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { ProcessOverviewVM, ProcessStepVM } from '@/utils/workflowOverview'
import { executorTone, type ExecutorTone } from '@/components/process/executorTone'
import { RESTRICTED_VALUE } from '@/components/process/restrictedText'
import { doneRowsAt, recordedFields, type LiveField } from '@/components/process/stepLive'
import { dueLine, lowerWords, outcomeWords, whenText } from '@/components/process/stepLiveText'
import { exitWord } from '@/components/process/stepDrawerText'
import { rowsOf } from '@/components/process/tableRows'
import { birthKind, birthWord } from './birthText'
import { REVISED, REVISION_WHY, reopenedHeading, stepHeading } from './passesText'
import { isPlace, placeWords } from './place'
import { isWindow, windowWords } from './window'

export const PRODUCED_MAX = 160

export type TimelineState = 'done' | 'reopened' | 'revised' | 'current' | 'upcoming' | 'closed'
export interface TimelineEntry {
  key: string
  stepId: string | null
  number: number | null
  title: string
  tone: ExecutorTone | null
  state: TimelineState
  meta: string
  /** The row's first line: "2 Qualify the counterparty", "<it> re-opened", "Revised", "Closed". */
  heading: string
  /** SA-1328: a visit's `occurred_at`, an event's `occurredAt`; null on current, upcoming and closed. */
  at: string | null
  /** A done visit's exit, and the step that exit leads to (a `step` target only). */
  exit: string | null
  exitTarget: { number: number; heading: string } | null
  produced: string | null
  aiLine: string | null
}
export interface Timeline { entries: TimelineEntry[]; more: number }
export interface TimelineContext {
  vm: ProcessOverviewVM
  definition: ProcessWorkflowDefinitionJson
  now?: Date
  /** SA-1418 (A4): the deployment zone the NOW entry's due time is printed in. */
  zone?: string
  /** SA-469 (spec D8): the record's source kind — the fallback when the entry step's completion carries no `via`. */
  sourceKind?: RecordSource['kind'] | null
}

/** One value, compactly: a table says how many rows, a redacted value says Restricted, a blank is nothing. */
export function fieldText(f: Pick<LiveField, 'type' | 'value' | 'redacted'>): string | null {
  if (f.redacted) return RESTRICTED_VALUE
  const v = f.value
  if (v === null || v === undefined || v === '') return null
  if (f.type === 'table') {
    const n = rowsOf(v).length
    return n === 0 ? null : `${n} row${n === 1 ? '' : 's'}`
  }
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : null
  if (isWindow(v)) return windowWords(v) || null        // SA-1136
  if (isPlace(v)) return placeWords(v) || null
  if (typeof v === 'object') return null
  if (f.type === 'enum' && typeof v === 'string') return lowerWords(v)
  return String(v)
}

/** "Counterparty type: broker · Cargo class: outsize", cut at PRODUCED_MAX with an ellipsis. */
export function producedSentence(fields: LiveField[]): string | null {
  const parts = fields
    .map((f) => { const t = fieldText(f); return t ? `${f.label}: ${t}` : null })
    .filter((p): p is string => p !== null)
  if (!parts.length) return null
  const s = parts.join(' · ')
  return s.length > PRODUCED_MAX ? `${s.slice(0, PRODUCED_MAX - 1).trimEnd()}…` : s
}

function head0(step: ProcessStepVM | undefined, id: string) {
  return step ? { stepId: step.id, number: step.number, title: step.title, tone: executorTone(step) } : { stepId: id, number: null, title: lowerWords(id), tone: null }
}
type Head = Pick<TimelineEntry, 'stepId' | 'number' | 'title' | 'tone' | 'heading'>
/** A step the definition lacks (a record pinned to an older version) still gets an entry, from its id. */
function head(step: ProcessStepVM | undefined, id: string): Head {
  const h = head0(step, id)
  return { ...h, heading: stepHeading(h.number, h.title) }
}
const blank = { at: null, exit: null, exitTarget: null, produced: null, aiLine: null } as const

function aiLineOf(ev: OrderEventItem | null): string | null {
  const p = ev?.payload
  if (!p) return null
  if (p.via === 'ai') return 'Completed by AI'
  if (p.accepted_proposal === true) return 'AI proposed, kept'
  if (p.accepted_proposal === false) return 'AI proposed, changed'
  return null
}

export function buildTimeline(record: RecordProcessRead, doneTasks: TaskRead[], events: OrderEventItem[], ctx: TimelineContext): Timeline {
  const { vm, definition, now, zone } = ctx
  const stepOf = (id: string) => vm.steps.find((s) => s.id === id)
  const asc = (a: OrderEventItem, b: OrderEventItem) => a.occurredAt.localeCompare(b.occurredAt)
  const completed = events.filter((e) => e.eventType === 'process_step_completed').sort(asc)
  const reopened = events.filter((e) => e.eventType === 'process_step_reopened').sort(asc)
  const revisions = events.filter((e) => e.eventType === 'process_revision_applied').sort(asc)
  const seen = new Map<string, number>()
  const rowsCache = new Map<string, TaskRead[]>()
  /** This step's rows, OLDEST first, so the k-th visit can read the k-th row. */
  const rowsFor = (id: string) => {
    if (!rowsCache.has(id)) rowsCache.set(id, doneRowsAt(doneTasks, id).reverse())
    return rowsCache.get(id)!
  }
  const visitsOf = (id: string) => record.path.filter((p) => p.step === id).length

  const items: { at: string; entry: TimelineEntry }[] = []
  for (const p of record.path) {
    const k = seen.get(p.step) ?? 0
    seen.set(p.step, k + 1)
    const step = stepOf(p.step)
    const rows = rowsFor(p.step)
    const total = visitsOf(p.step)
    // The visit's row: the k-th when rows and visits pair one to one; the
    // LAST visit always reads the newest row (the drawer's rule); otherwise
    // no row, and the bag speaks — never a guess at which row was which.
    const row = rows.length === total ? rows[k] : k === total - 1 ? rows[rows.length - 1] ?? null : null
    // `events` is the newest 500 rows (`useRecordEvents`); on a record with
    // more history the earliest visits pair with the wrong completion event
    // and only `aiLine` can mis-set — the entries themselves come from
    // `record.path`, which the server supplies whole.
    const ev = completed.filter((e) => e.payload?.step === p.step)[k] ?? null
    const target = step && p.exit ? step.exits.find((e) => e.name === p.exit)?.targetLabel ?? null : null
    const meta = step?.isEntry && k === 0
      // SA-469 (spec §3): "Enquiry recorded · email · 23 Sep 10:14 → 2 Qualify the counterparty".
      ? (() => {
          const born = birthKind(ev?.payload?.via, ctx.sourceKind)
          const head = ['Enquiry recorded', born ? birthWord(born) : null, whenText(p.occurred_at, now)].filter(Boolean).join(' · ')
          return target ? `${head} → ${target}` : head
        })()
      : [
          row?.seat_label ?? step?.who ?? null,
          whenText(p.occurred_at, now),
          p.exit ? `${exitWord(p.exit)}${target ? ` → ${target}` : ''}` : null,
        ].filter(Boolean).join(' · ')
    const produced = step ? producedSentence(recordedFields(record, definition, step, row?.result ? [row] : [])) : null
    const exitStep = step && p.exit ? step.exits.find((e) => e.name === p.exit) : undefined
    const targetStep = exitStep?.targetKind === 'step' ? stepOf(exitStep.target) : undefined
    const exitTarget = targetStep ? { number: targetStep.number, heading: stepHeading(targetStep.number, targetStep.title) } : null
    items.push({ at: p.occurred_at, entry: { key: `${p.step}#${k}`, ...head(step, p.step), state: 'done', meta, ...blank, at: p.occurred_at, exit: p.exit ?? null, exitTarget, produced, aiLine: aiLineOf(ev) } })
  }
  for (const e of reopened) {
    const id = typeof e.payload?.step === 'string' ? e.payload.step : ''
    const reason = typeof e.payload?.reason === 'string' && e.payload.reason ? e.payload.reason : null
    const meta = [whenText(e.occurredAt, now), reason].filter(Boolean).join(' · ')
    const h = head(stepOf(id), id)
    items.push({ at: e.occurredAt, entry: { key: `reopen#${e.id}`, ...h, heading: reopenedHeading(h.heading), state: 'reopened', meta, ...blank, at: e.occurredAt } })
  }
  for (const e of revisions) {
    const n = e.payload?.revision_number
    // SA-1044: a revision that stayed (`resumed_at` null) moved the process nowhere, so the List draws no entry.
    if (typeof e.payload?.resumed_at !== 'string' || !e.payload.resumed_at) continue
    const id = e.payload.resumed_at
    // A revision with no number pairs with nothing: `undefined === undefined` would match any event that lacks one too.
    const numbered = typeof n === 'number'
    const same = (type: string) => numbered && events.some((x) => x.eventType === type && x.payload?.revision_number === n)
    const why = same('customer_update_applied')
      ? REVISION_WHY.customer_update
      : numbered && events.some((x) => x.eventType === 'revised_analysis_started' && x.actorType === 'staff' && x.payload?.revision_number === n)
        ? REVISION_WHY.intake
        : null
    const back = stepOf(id)
    const meta = [whenText(e.occurredAt, now), why, back ? `back to ${stepHeading(back.number, back.title)}` : null].filter(Boolean).join(' · ')
    const h = head(back, id)
    items.push({ at: e.occurredAt, entry: { key: `revision#${e.id}`, ...h, heading: REVISED, state: 'revised', meta, ...blank, at: e.occurredAt } })
  }
  // Stable: a visit stamped in the same instant as a re-open stays before it.
  items.sort((a, b) => a.at.localeCompare(b.at))
  const entries = items.map((i) => i.entry)

  let more = 0
  const cs = record.current_step
  if (record.status === 'closed') {
    entries.push({ key: 'closed', stepId: null, number: null, title: 'Closed', heading: 'Closed', tone: null, state: 'closed', meta: outcomeWords(record.closed_outcome) ?? '', ...blank })
  } else if (cs) {
    const meta = [
      cs.seat_label ? `waiting on ${cs.seat_label}` : null,
      cs.waiting_for_event ? lowerWords(cs.waiting_for_event) : null,
      cs.due_at ? `due ${dueLine(cs.due_at, now, zone)}` : null,
      cs.overdue ? 'overdue' : null,
    ].filter(Boolean).join(' · ')
    entries.push({ key: `${cs.id}#current`, ...head(stepOf(cs.id), cs.id), state: 'current', meta, ...blank })
    const idx = vm.steps.findIndex((s) => s.id === cs.id)
    if (idx >= 0) {
      const next = vm.steps.slice(idx + 1, idx + 3)
      for (const s of next) {
        entries.push({ key: `${s.id}#upcoming`, ...head(s, s.id), state: 'upcoming', meta: [s.who, s.due ? `due ${s.due}` : null].filter(Boolean).join(' · '), ...blank })
      }
      more = Math.max(0, vm.steps.length - idx - 1 - next.length)
    }
  }
  return { entries, more }
}
